import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, Sparkles, ShieldCheck, Crown } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const FREE = [
  "Resume upload & parsing",
  "ATS review (score + issues + fixes)",
  "1 CV — build & unlimited edits",
  "PDF download (with Cavilow watermark)",
  "3 free job searches",
  "Tutorial — how to use Cavilow",
];
const PRO = [
  "Unlimited CVs & tailored versions",
  "50+ premium resume templates",
  "Generate improved & multi-version resumes",
  "Tailor resume to any job description",
  "Cover letter writer (5 tones)",
  "Interview practice (40+ questions / role)",
  "Unlimited job searches",
  "Application Tracker (Kanban)",
  "PDF & DOCX downloads — no watermark",
  "Priority generation queue",
];

function formatPrice(amount, currency) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState("monthly"); // monthly | yearly
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  // Handle return from Stripe
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get("session_id");
    if (!sid) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          toast.success("Welcome to Cavilow Premium!");
          refresh();
          window.history.replaceState(null, "", "/pricing");
          return;
        }
        if (r.data.status === "expired") {
          toast.error("Payment session expired");
          window.history.replaceState(null, "", "/pricing");
          return;
        }
        attempts++;
        if (attempts < 6) setTimeout(poll, 2000);
        else toast.message("Payment is still processing.");
      } catch {
        if (attempts === 0) toast.error("Could not verify payment");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        plan: "premium",
        interval,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const isPremium = user?.subscription === "premium";
  const isAdmin = user?.is_admin;

  const tier = pricing?.[interval];
  const priceLabel = tier ? formatPrice(tier.amount, tier.currency) : "—";
  const altUSD = pricing?.country === "United Arab Emirates" || pricing?.country === "India"
    ? (interval === "monthly" ? "USD 3" : "USD 33")
    : null;

  return (
    <div className="space-y-10">
      <div className="text-center fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#80FFEA]">Pricing</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Simple, regional pricing.</h1>
        <p className="mt-3 text-slate-400 max-w-xl mx-auto">
          Upload your resume and run a full ATS review free, forever. Unlock everything else with Cavilow Premium.
        </p>
      </div>

      {/* Admin notice */}
      {isAdmin && (
        <div className="max-w-2xl mx-auto rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="admin-notice">
          <Crown size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900 leading-relaxed">
            You&apos;re signed in as the <b>Cavilow admin</b>. All features are unlocked at no charge — no payment required.
          </p>
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex justify-center fade-up-delay-1">
        <div className="inline-flex glass p-1">
          <button
            data-testid="interval-monthly"
            onClick={() => setInterval("monthly")}
            className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors ${interval === "monthly" ? "bg-[var(--cavilo-ink)] text-white" : "text-slate-300 hover:text-white"}`}
          >
            Monthly
          </button>
          <button
            data-testid="interval-yearly"
            onClick={() => setInterval("yearly")}
            className={`px-5 py-2 rounded-xl text-sm font-bold transition-colors inline-flex items-center gap-2 ${interval === "yearly" ? "bg-[var(--cavilo-ink)] text-white" : "text-slate-300 hover:text-white"}`}
          >
            Yearly <span className="chip chip-mint" style={{ padding: "2px 8px" }}>Save 25%</span>
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto fade-up-delay-2">
        <div className="glass p-8" data-testid="plan-free-card">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Free</div>
          <div className="mt-2 flex items-end gap-1">
            <span className="font-display font-black text-5xl">$0</span>
            <span className="text-slate-400 mb-2 text-sm">/ forever</span>
          </div>
          <p className="text-sm text-slate-400 mt-1">Everything to start your search.</p>
          <ul className="mt-6 space-y-3 text-sm">
            {FREE.map((f) => (
              <li key={f} className="flex items-start gap-2 text-slate-200">
                <Check size={16} className="text-[#80FFEA] mt-0.5 shrink-0" /> {f}
              </li>
            ))}
          </ul>
          <button
            data-testid="plan-free"
            disabled
            className="mt-8 w-full rounded-xl bg-white border-2 border-white/10 py-3 font-semibold text-sm text-slate-300"
          >
            {isPremium ? "Included" : "Your current plan"}
          </button>
        </div>

        <div className="rounded-2xl bg-[var(--cavilo-ink)] text-white p-8 relative overflow-hidden" data-testid="plan-premium-card">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[var(--cavilo-blue)]/30" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-200">Premium</span>
              <span className="text-[10px] font-bold uppercase tracking-wider bg-[var(--cavilo-blue)] text-white px-2 py-0.5 rounded-full">
                <Sparkles size={10} className="inline -mt-0.5 mr-0.5" /> Recommended
              </span>
            </div>
            <div className="mt-2 flex items-end gap-1">
              <span className="font-display font-black text-5xl" data-testid="plan-premium-price">{priceLabel}</span>
              <span className="text-slate-300 mb-2 text-sm">/ {interval === "yearly" ? "year" : "month"}</span>
            </div>
            {altUSD && (
              <p className="text-xs text-slate-400 mt-1">≈ {altUSD} {interval === "yearly" ? "/yr" : "/mo"}</p>
            )}
            <p className="text-sm text-slate-300 mt-1">For the serious job hunt.</p>
            <ul className="mt-6 space-y-3 text-sm">
              {PRO.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-100">
                  <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="plan-premium-checkout"
              onClick={startCheckout}
              disabled={busy || isPremium || isAdmin}
              className="mt-8 w-full rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] py-3 font-bold text-sm shadow-lg shadow-blue-500/30 disabled:opacity-60"
            >
              {isAdmin ? "Admin — included" : isPremium ? "You're on Premium" : busy ? "Redirecting…" : `Upgrade — ${priceLabel}`}
            </button>
            <p className="mt-3 text-[11px] text-slate-400 text-center flex items-center justify-center gap-1.5">
              <ShieldCheck size={11} /> Secure checkout via Stripe — cancel anytime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
